// Tickets (packages/shared domain/ticket.ts): your own, sent from Settings, and every one for the
// administrators who accept or deny them (Administration › Tickets), and their screenshots: a picture is
// prepared in the browser first (a big one scaled down to a JPEG, collab/chatUploads.ts), then sent on its own.
// The worker routes are in apps/worker/src/routes/tickets.ts.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Ticket, TicketCreate, TicketDecide, TicketFile } from '@technohull/shared';
import { api } from '../lib/api.js';

const MINE = ['tickets', 'mine'] as const;
const ALL = ['tickets', 'all'] as const;
type List = { tickets: Ticket[] };

/** your own tickets, newest first */
export function useMyTickets() {
  return useQuery({ queryKey: MINE, queryFn: () => api.get<List>('/api/tickets/mine'), staleTime: 30_000, refetchOnWindowFocus: true });
}

/** every ticket, newest first (admin:tickets) */
export function useAllTickets(enabled = true) {
  return useQuery({ queryKey: ALL, queryFn: () => api.get<List>('/api/admin/tickets'), enabled, staleTime: 15_000, refetchOnWindowFocus: true });
}

/** a ticket with its new state put into whichever lists hold it */
function useSettle() {
  const qc = useQueryClient();
  return (t: Ticket, add = false) => {
    for (const key of [MINE, ALL]) {
      qc.setQueryData<List>(key, (d) => {
        if (!d) return d;
        const has = d.tickets.some((x) => x.id === t.id);
        if (has) return { tickets: d.tickets.map((x) => (x.id === t.id ? t : x)) };
        return add ? { tickets: [t, ...d.tickets] } : d;
      });
    }
    void qc.invalidateQueries({ queryKey: ['tickets'] });
  };
}

export function useCreateTicket() {
  const settle = useSettle();
  return useMutation({
    mutationFn: (v: TicketCreate) => api.post<{ ok: true; ticket: Ticket }>('/api/tickets', v),
    onSuccess: (res) => settle(res.ticket, true),
  });
}

export function useWithdrawTicket() {
  const settle = useSettle();
  return useMutation({
    mutationFn: (id: number) => api.post<{ ok: true; ticket: Ticket }>('/api/tickets/withdraw', { id }),
    onSuccess: (res) => settle(res.ticket),
  });
}

export function useDecideTicket() {
  const settle = useSettle();
  return useMutation({
    mutationFn: (v: TicketDecide) => api.post<{ ok: true; ticket: Ticket }>('/api/admin/tickets/decide', v),
    onSuccess: (res) => settle(res.ticket),
  });
}

/** put one prepared screenshot on an open ticket of yours */
export function uploadTicketFile(
  ticketId: number,
  file: { blob: Blob; name: string; type: string; width: number | null; height: number | null },
  opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
) {
  const q = new URLSearchParams({ ticket: String(ticketId), name: file.name });
  if (file.width && file.height) { q.set('w', String(file.width)); q.set('h', String(file.height)); }
  return api.uploadWithProgress<{ ok: true; file: TicketFile }>(`/api/tickets/file?${q}`, file.blob, file.type || 'application/octet-stream', opts);
}

/** the lists again, after screenshots went on (or came off) a ticket */
export function useRefreshTickets() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['tickets'] });
}

/** take a screenshot off an open ticket of yours */
export function useRemoveTicketFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true }>('/api/tickets/file/delete', { id }),
    onSuccess: (_r, id) => {
      for (const key of [MINE, ALL]) {
        qc.setQueryData<List>(key, (d) => (d ? { tickets: d.tickets.map((t) => ({ ...t, files: t.files.filter((f) => f.id !== id) })) } : d));
      }
      void qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}
